<template>
    <TableRow @dblclick="toggleExpanded"
        :isSelected="isSelected" class="transition-all duration-300" :class="[{'cursor-wait': loadingCompany, 'opacity-60': isRetired}]"
    >
        <TableCell>
            <div class="flex justify-center items-center p-2 w-10">
                <button
                    type="button"
                    @click.stop="openEditModal"
                    class="whitespace-nowrap text-xs text-gray-500 hover:text-accent-primary hover:underline cursor-pointer"
                    :title="`Edit item #${inventoryItem.id}`"
                >
                    {{ inventoryItem.id }}
                </button>
            </div>

        </TableCell>
        <TableCell>
            <div class="flex justify-center items-center p-2 text-lg" :title="categoryName || 'No category'">
                {{ categoryIcon }}
            </div>
        </TableCell>
        <TableCell>
            <div
                v-if="editingField !== 'asset_name'"
                @dblclick.stop="startEdit('asset_name', assetNameLocal || '')"
                :class="[{'truncate': truncating}, {'max-h-10' : !expanded, 'max-h-96': expanded}]"
                class="transition-all duration-300 w-full p-2 font-medium font-mono hover:bg-gray-50 cursor-text"
                :title="'Double-click to edit device name'"
            >
                {{ assetNameLocal || '--' }}
            </div>
            <input
                v-else
                ref="assetNameInput"
                v-model="editValue"
                @blur="commitEdit('asset_name')"
                @keyup.enter="commitEdit('asset_name')"
                @keyup.escape="cancelEdit"
                @click.stop
                @dblclick.stop
                class="w-full p-1 border border-accent-primary rounded text-xs font-mono"
            />
        </TableCell>
        <TableCell>
            <div
                v-if="editingField !== 'old_device_name'"
                @dblclick.stop="startEdit('old_device_name', oldDeviceName || '')"
                :class="[{'truncate': truncating}, {'max-h-10' : !expanded, 'max-h-96': expanded}]"
                class="transition-all duration-300 w-full p-2 font-mono hover:bg-gray-50 cursor-text text-gray-600"
                :title="'Double-click to edit old name'"
            >
                {{ oldDeviceName || '--' }}
            </div>
            <input
                v-else
                ref="oldNameInput"
                v-model="editValue"
                @blur="commitEdit('old_device_name')"
                @keyup.enter="commitEdit('old_device_name')"
                @keyup.escape="cancelEdit"
                @click.stop
                @dblclick.stop
                class="w-full p-1 border border-accent-primary rounded text-xs font-mono"
            />
        </TableCell>
        <TableCell>
            <div :class="[{'truncate': truncating}, {'max-h-10' : !expanded, 'max-h-96': expanded}]" class="transition-all duration-300 w-full p-2 font-mono">
                {{ serialNumber }}
            </div>
        </TableCell>
        <TableCell>
            <div class="flex justify-center items-center p-2">
                <input
                    type="checkbox"
                    :checked="action1Installed"
                    @click.stop
                    @change="toggleBool('action1_installed', $event.target.checked)"
                    :title="'Action1 installed?'"
                    class="cursor-pointer w-6 h-6 accent-gray-600"
                />
            </div>
        </TableCell>
        <TableCell>
            <div class="flex justify-center items-center p-2">
                <input
                    type="checkbox"
                    :checked="action1SpecsCaptured"
                    @click.stop
                    @change="toggleBool('action1_specs_captured', $event.target.checked)"
                    :title="'Specs captured via Action1?'"
                    class="cursor-pointer w-6 h-6 accent-gray-600"
                />
            </div>
        </TableCell>
        <TableCell>
            <div
                v-if="editingField !== 'next_steps'"
                @dblclick.stop="startEdit('next_steps', nextSteps || '')"
                :class="[{'truncate': truncating}, {'max-h-10' : !expanded, 'max-h-96': expanded}]"
                class="transition-all duration-300 w-full p-2 hover:bg-gray-50 cursor-text"
                :title="'Double-click to edit next steps'"
            >
                {{ nextSteps || '--' }}
            </div>
            <input
                v-else
                ref="nextStepsInput"
                v-model="editValue"
                @blur="commitEdit('next_steps')"
                @keyup.enter="commitEdit('next_steps')"
                @keyup.escape="cancelEdit"
                @click.stop
                @dblclick.stop
                class="w-full p-1 border border-accent-primary rounded text-xs"
            />
        </TableCell>
        <TableCell>
            <div class="flex flex-col p-2 space-y-0.5">
                <span class="text-xs px-1.5 py-0.5 rounded text-center bg-slate-300 text-slate-800" :title="`Lifecycle: ${lifecycleLabel}`">{{ lifecycleLabel }}</span>
                <span v-if="showWarrantyChip" class="text-[10px] px-1.5 py-0.5 rounded text-center" :class="warrantyBadgeClass" :title="`Warranty: ${warrantyStatusLabel}`">W: {{ warrantyAbbr }}</span>
                <span v-if="openIssueCount > 0" class="text-[10px] px-1.5 py-0.5 rounded text-center bg-slate-300 text-slate-800" :title="`${openIssueCount} open issue(s)`">⚠ {{ openIssueCount }}</span>
            </div>
        </TableCell>
        <TableCell>
            <div
                v-if="editingField !== 'purpose'"
                @dblclick.stop="startEdit('purpose', purpose || '')"
                :class="[{'truncate': truncating}, {'max-h-10' : !expanded, 'max-h-96': expanded}]"
                class="transition-all duration-300 w-full p-2 hover:bg-gray-50 cursor-text"
                :title="'Double-click to edit purpose (e.g. full time, intern, spare, server for accounting)'"
            >
                {{ purpose || '--' }}
            </div>
            <input
                v-else
                ref="purposeInput"
                v-model="editValue"
                @blur="commitEdit('purpose')"
                @keyup.enter="commitEdit('purpose')"
                @keyup.escape="cancelEdit"
                @click.stop
                @dblclick.stop
                class="w-full p-1 border border-accent-primary rounded text-xs"
            />
        </TableCell>
        <TableCell>
            <div @click="openAssignmentHistory" :class="[{'truncate': truncating}, {'max-h-10' : !expanded, 'max-h-96': expanded}]" class="transition-all duration-300 hover:underline hover:cursor-pointer w-full p-2">
                {{ issuedOn }}
            </div>
        </TableCell>
        <TableCell>
            <div :class="[{'truncate': truncating}, {'max-h-10' : !expanded, 'max-h-96': expanded}]" class="transition-all duration-300 w-full p-2">
                {{ brand }}
            </div>
        </TableCell>
        <TableCell>
            <div
                :class="[{'truncate': truncating}, {'max-h-10' : !expanded, 'max-h-96': expanded}, {'cursor-help': laptopSpecTooltip}]"
                class="transition-all duration-300 w-full p-2"
                @mouseenter="showSpecTooltip"
                @mousemove="moveSpecTooltip"
                @mouseleave="hideSpecTooltip"
            >
                {{ model }}
                <Teleport to="body">
                    <div
                        v-if="specTooltipVisible && laptopSpecTooltip"
                        :style="{ left: specTooltipPos.x + 'px', top: specTooltipPos.y + 'px' }"
                        class="fixed z-[100] bg-dark-primary text-white text-xs rounded shadow-lg p-2 whitespace-pre-line min-w-[260px] max-w-md pointer-events-none"
                    >{{ laptopSpecTooltip }}</div>
                </Teleport>
            </div>
        </TableCell>
        <TableCell>
            <div
                v-if="editingField !== 'notes'"
                @dblclick.stop="startEdit('notes', notes || '')"
                :class="[{'truncate': truncating}, {'max-h-10' : !expanded, 'max-h-96': expanded}]"
                class="transition-all duration-300 w-full p-2 whitespace-pre-line hover:bg-gray-50 cursor-text"
                :title="'Double-click to edit notes'"
            >
                {{ notes || '--' }}
            </div>
            <textarea
                v-else
                ref="notesInput"
                v-model="editValue"
                @blur="commitEdit('notes')"
                @keyup.escape="cancelEdit"
                @click.stop
                @dblclick.stop
                class="w-full p-1 border border-accent-primary rounded text-xs"
                rows="3"
            ></textarea>
        </TableCell>
        <TableCell>
            <div :class="[{'truncate': truncating}, {'max-h-10' : !expanded, 'max-h-96': expanded}]" class="transition-all duration-300 w-full p-2">
                {{ cost }}
            </div>
        </TableCell>
        <TableCell >
            <div :class="[{'max-h-10' : !expanded, 'max-h-96': expanded}]" class="transition-all duration-300 w-full flex  p-2">
                <div class="w-full flex justify-between items-center">
                    <button
                        v-if="isLoading"
                        disabled 
                        class="border border-gray-400  bg-gray-400 whitespace-nowrap flex justify-center items-center rounded-md"
                    >
                        <InfiniteSpinnerSVG icon-class="h-4" />
                    </button>
                    <button v-else 
                        @click="openEditModal"
                        class="bg-none border border-dark-secondary hover:bg-dark-primary hover:text-gray-100 hover:shadow active:bg-dark-primary active:text-gray-400 text-white bg-dark-secondary whitespace-nowrap flex justify-center items-center rounded-md"
                    >
                    <EditSVG stroke="white" /> 
                    </button>
                    <button
                        v-if="isLoading"
                        disabled 
                        class="border border-gray-400  bg-gray-400 whitespace-nowrap flex justify-center items-center rounded-md"
                    >
                        <InfiniteSpinnerSVG icon-class="h-4" />
                    </button>
                    <button v-else 
                        @click="deleteItem"
                        class="bg-none border border-dark-secondary hover:bg-dark-primary hover:text-gray-100 hover:shadow active:bg-dark-primary active:text-gray-400 text-white bg-dark-secondary whitespace-nowrap flex justify-center items-center rounded-md"
                    >
                    <TrashSVG fill="white" />  
                    </button>
                <!-- <button  @click="openEditModal" class="bg-dark-secondary text-white rounded-md py-0.5 px-1 hover:invert hover:shadow-md">
                    <EditSVG stroke="white" />
                </button> -->
                <!-- <button :disabled="isLoading"
            class="bg-dark-secondary text-white rounded-md ml-2 py-0.5 px-1 hover:invert hover:shadow-md"
            @click="deleteItem"
        >
        <TrashSVG fill="white" />   
        </button> -->
            </div>
                <!-- <base-button @click="openEditModal"><EditSVG /></base-button> -->
                <!-- <base-button-negative @click="deleteItem"><TrashSVG /></base-button-negative> -->
            </div>
            <base-modal :is-form-visible="isFormVisible" @close="closeEditModal">
                <template #header>
                    <h2 class="text-lg">
                    Edit {{ itemName }}
                    </h2>
                </template>
                <template #body>
                    <InventoryItemModal
                        :inventory-item="inventoryItem"
                        :user-options="userOptions"
                        :inventory-category-options="inventoryCategoryOptions"
                        :initial-tab="initialEditTab"
                        @refetch-inventory-items="refetchResults"
                        @close-edit-form="closeEditModal"
                    />
                </template>
            </base-modal>
        </TableCell>
    </TableRow>
</template>

<script setup>
import {ref, defineProps, defineEmits, computed, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { formatCurrency } from "@/composables/formatCurrency";
import TableRow from '@/components/base-components/TableRow.vue';
import TableCell from '@/components/base-components/TableCell.vue';
import InventoryItemModal from "./InventoryItemModal.vue";
import { useToast } from 'vue-toast-notification';
import { formatDateTwoDigitYear } from "@/composables/formatDate";
import TrashSVG from "../svg-components/TrashSVG.vue";
import EditSVG from "../svg-components/EditSVG.vue";
import InfiniteSpinnerSVG from "../svg-components/InfiniteSpinnerSVG.vue";
import { patchInventoryItem } from '@/services/Inventory';


const store = useStore()
const $toast = useToast()

const props = defineProps([
    'inventoryItem',
    'selectAll',
    'isLoading',
    'userOptions',
    'inventoryCategoryOptions'
])

const emit = defineEmits([
    'refetch-results', 
    'loading',
    'delete-inventory-item'
])

// const rejectionReason = ref(props.companyCampaignChangeRequest.rejection_reason)
const loadingCompany = ref(false)
const isSelected = ref(props.selectAll)
const visibleSelectBox = ref(false)
const isFormVisible = ref(false)
const initialEditTab = ref('edit')


function toggleExpanded(){
    expanded.value = !expanded.value
}

function openEditModal(){
    initialEditTab.value = 'edit'
    isFormVisible.value = true
}

function openAssignmentHistory(){
    initialEditTab.value = 'assignment'
    isFormVisible.value = true
}

function closeEditModal(){
    isFormVisible.value = false
}

function deleteItem(){
    if(confirm("Are you sure you want to delete this inventory item?")){
        emit('delete-inventory-item', props.inventoryItem.id)
    }
}

watch(() => props.selectAll, () => {
    isSelected.value = props.selectAll
    if(props.selectAll) {
        showSelectBox()
        // emit('add-selected-company-campaign-change-request', props.companyCampaignChangeRequest)
    } else if (!props.selectAll) {
        hideSelectBox()
        // emit('remove-selected-company-campaign-change-request', props.companyCampaignChangeRequest)
    }
})

const mostRecentOwner = computed(() => {
    if(props.inventoryItem && props.inventoryItem.inventory_users.length > 0){
        return props.inventoryItem.inventory_users[0]
    } else {
        return null
    }
})

const lifecycleState = computed(() => props.inventoryItem?.lifecycle_state || 'in_stock');
const isRetired = computed(() => lifecycleState.value === 'retired');
const lifecycleLabel = computed(() => ({
    in_stock: 'available', assigned: 'assigned',
    returned: 'needs review', retired: 'retired',
}[lifecycleState.value] || lifecycleState.value));
const lifecycleBadgeClass = computed(() => ({
    in_stock: 'bg-green-100 text-green-800',
    assigned: 'bg-blue-100 text-blue-800',
    returned: 'bg-amber-100 text-amber-800',
    retired: 'bg-gray-300 text-gray-700',
}[lifecycleState.value] || 'bg-gray-100 text-gray-700'));

const warrantyStatus = computed(() => props.inventoryItem?.warranty_status);
const warrantyStatusLabel = computed(() => ({
    active: 'active', expiring_soon: 'expiring soon',
    expired: 'expired', unknown: 'unknown',
}[warrantyStatus.value] || warrantyStatus.value));
const warrantyAbbr = computed(() => ({
    active: 'active', expiring_soon: 'expiring',
    expired: 'expired', unknown: 'no info',
}[warrantyStatus.value] || ''));
const warrantyBadgeClass = computed(() => ({
    expiring_soon: 'bg-amber-50 text-amber-800',
    expired: 'bg-red-50 text-red-800',
}[warrantyStatus.value] || 'bg-gray-100 text-gray-700'));

// Only surface the warranty pill when there's something to act on.
const showWarrantyChip = computed(() =>
    warrantyStatus.value === 'expiring_soon' || warrantyStatus.value === 'expired'
);

const openIssueCount = computed(() => props.inventoryItem?.open_issue_count || 0);

// Local copies of the inline-editable fields so we can do optimistic updates
// without mutating props or refetching the grid on every keystroke.
const assetNameLocal = ref(props.inventoryItem?.asset_name || '');
const oldDeviceName = ref(props.inventoryItem?.old_device_name || '');
const purpose = ref(props.inventoryItem?.purpose || '');
const nextSteps = ref(props.inventoryItem?.next_steps || '');
const notes = ref(props.inventoryItem?.notes || '');
const action1Installed = ref(!!props.inventoryItem?.action1_installed);
const action1SpecsCaptured = ref(!!props.inventoryItem?.action1_specs_captured);

const fieldRefs = {
    asset_name: assetNameLocal,
    old_device_name: oldDeviceName,
    purpose,
    next_steps: nextSteps,
    notes,
    action1_installed: action1Installed,
    action1_specs_captured: action1SpecsCaptured,
};

// Resync local copies if the parent reloads the row.
watch(() => props.inventoryItem, (item) => {
    if (!item) return;
    assetNameLocal.value = item.asset_name || '';
    oldDeviceName.value = item.old_device_name || '';
    purpose.value = item.purpose || '';
    nextSteps.value = item.next_steps || '';
    notes.value = item.notes || '';
    action1Installed.value = !!item.action1_installed;
    action1SpecsCaptured.value = !!item.action1_specs_captured;
}, { deep: true });

// --- Inline text editing for asset_name + old_device_name + purpose + next_steps ---
const editingField = ref(null);
const editValue = ref('');
const assetNameInput = ref(null);
const oldNameInput = ref(null);
const purposeInput = ref(null);
const nextStepsInput = ref(null);
const notesInput = ref(null);

function startEdit(field, currentValue) {
    editingField.value = field;
    editValue.value = currentValue;
    nextTick(() => {
        const el = (
            field === 'asset_name' ? assetNameInput.value
            : field === 'old_device_name' ? oldNameInput.value
            : field === 'next_steps' ? nextStepsInput.value
            : field === 'notes' ? notesInput.value
            : purposeInput.value
        );
        if (el) {
            el.focus();
            el.select();
        }
    });
}

function cancelEdit() {
    editingField.value = null;
    editValue.value = '';
}

async function commitEdit(field) {
    if (editingField.value !== field) return; // blur after enter — already handled
    const targetRef = fieldRefs[field];
    const oldValue = targetRef.value || '';
    const newValue = (editValue.value || '').trim();
    editingField.value = null;
    if (newValue === oldValue) return;
    // Optimistic update.
    targetRef.value = newValue;
    try {
        await patchInventoryItem(
            props.inventoryItem.id,
            { [field]: newValue || null },
            store.state.auth.token,
        );
    } catch (e) {
        targetRef.value = oldValue;
        const msg = e?.response?.data
            ? (typeof e.response.data === 'string' ? e.response.data : JSON.stringify(e.response.data))
            : e?.message || String(e);
        $toast.error(`Couldn't save ${field}: ${msg}`);
    }
}

// --- Inline checkbox toggle for action1_* booleans ---
async function toggleBool(field, checked) {
    const targetRef = fieldRefs[field];
    const prev = targetRef.value;
    targetRef.value = checked;
    try {
        await patchInventoryItem(
            props.inventoryItem.id,
            { [field]: checked },
            store.state.auth.token,
        );
    } catch (e) {
        targetRef.value = prev;
        $toast.error(`Couldn't save ${field}: ${e?.message || e}`);
    }
}


const brand = computed(() => {
    if(props.inventoryItem && props.inventoryItem.brand) {
        return props.inventoryItem.brand
    } else {
        return '--'
    }
})

const model = computed(() => {
    if(props.inventoryItem && props.inventoryItem.model) {
        return props.inventoryItem.model
    } else {
        return '--'
    }
})

const categoryName = computed(() => props.inventoryItem?.category?.category_name || '');

// Map each category to an emoji. Match by lowercased name so renames /
// new categories are easy. Default to a box.
const CATEGORY_ICON_MAP = {
    'laptop': '💻',
    'computer monitor': '🖥️',
    'monitor': '🖥️',
    'keyboard/mouse': '⌨️',
    'keyboard': '⌨️',
    'mouse': '🖱️',
    'docking hub': '🔌',
    'dock': '🔌',
    'key card': '🪪',
    'key': '🔑',
    'laptop bag': '💼',
    'bag': '💼',
    'printer': '🖨️',
    'upc': '⚡',
    'ups': '⚡',
    'other': '📦',
};

const categoryIcon = computed(() => {
    const key = categoryName.value.toLowerCase();
    return CATEGORY_ICON_MAP[key] || '📦';
});

// Hover tooltip content for the Model cell — populated laptop spec fields.
const laptopSpecTooltip = computed(() => {
    const i = props.inventoryItem;
    if (!i) return '';
    const lines = [];
    if (i.processor) lines.push(`CPU: ${i.processor}`);
    if (i.memory) lines.push(`Memory: ${i.memory}`);
    if (i.storage) lines.push(`Storage: ${i.storage}`);
    if (i.screen_size) lines.push(`Screen: ${i.screen_size}`);
    if (i.gpu) lines.push(`GPU: ${i.gpu}`);
    if (i.mac_address) lines.push(`MAC: ${i.mac_address}`);
    return lines.join('\n');
});

// Tooltip state. We render the popover via <Teleport to="body"> so the
// TableCell's overflow-hidden doesn't clip it, and follow the cursor for a
// natural feel.
const specTooltipVisible = ref(false);
const specTooltipPos = ref({ x: 0, y: 0 });

function showSpecTooltip(e) {
    if (!laptopSpecTooltip.value) return;
    specTooltipVisible.value = true;
    specTooltipPos.value = { x: e.clientX + 14, y: e.clientY + 14 };
}
function moveSpecTooltip(e) {
    if (!specTooltipVisible.value) return;
    specTooltipPos.value = { x: e.clientX + 14, y: e.clientY + 14 };
}
function hideSpecTooltip() {
    specTooltipVisible.value = false;
}

const description = computed(() => {
    if(props.inventoryItem && props.inventoryItem.description) {
        return props.inventoryItem.description
    } else {
        return '--'
    }
})

const itemName = computed(() => {
    if(props.inventoryItem) {
        return props.inventoryItem.item_name
    } else {
        return '--'
    }
})
const condition = computed(() => {
    if(props.inventoryItem && props.inventoryItem.condition) {
        return props.inventoryItem.condition
    } else {
        return '--'
    }
})
const cost = computed(() => {
    if(props.inventoryItem) {
        return formatCurrency(Math.round(Number(props.inventoryItem.cost || 0)))
    } else {
        return '--'
    }
})
const serialNumber = computed(() => {
    if(props.inventoryItem && props.inventoryItem.serial_number) {
        return props.inventoryItem.serial_number
    } else {
        return '--'
    }
})

const location = computed(() => {
    if(props.inventoryItem && props.inventoryItem.location){
        return props.inventoryItem.location
    } else {
        return '--'
    }
})


const issuedOn = computed(() => {
    // Only count an OPEN assignment (returned_on is null). When the most-recent
    // holder has returned the item, the item is back in general inventory —
    // not still "issued to" them.
    const open = (props.inventoryItem?.inventory_users || []).find(a => a.returned_on === null);
    if (open && open.user) {
        return `${formatDateTwoDigitYear(open.issued_on)}: ${open.user.first_name} ${open.user.last_name}`;
    }
    return 'General Inventory';
});

const returnedOn = computed(() => {
    if(mostRecentOwner.value && mostRecentOwner.value.returned_on){
        return formatDateTwoDigitYear(mostRecentOwner.value.returned_on)
    } else {
        return '--'
    }
})


function toggleSelected() {
    if(isSelected.value) {
        isSelected.value = false
        // emit('remove-selected-company-campaign-change-request', props.companyCampaignChangeRequest)
    } else {
        isSelected.value = true
        // emit('add-selected-company-campaign-change-request', props.companyCampaignChangeRequest)
    }
}

function refetchResults() {
    emit('refetch-results')
}


function showSelectBox() {
    visibleSelectBox.value = true
}

function hideSelectBox() {
    if(!props.selectAll & !isSelected.value) {
        visibleSelectBox.value = false
    }
}
const expanded = ref(false)
const truncating = ref(true)

watch(expanded, () => {
    if(expanded.value) {
        truncating.value = false
    } else {
        setTimeout(() => {
            truncating.value = true
        }, 300)

    }
})




</script>